import React, { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Button,
  Grid,
  Card,
  CardContent,
  CardActions,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  IconButton,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  ListItemIcon,
  Divider,
  Alert,
  Snackbar,
  CircularProgress,
  useTheme,
  alpha,
  InputAdornment,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DescriptionIcon from '@mui/icons-material/Description';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import TableChartIcon from '@mui/icons-material/TableChart';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import GetAppIcon from '@mui/icons-material/GetApp';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { uploadScopeItemsExcel, downloadScopeItemTemplate } from '../utils/workshopService';
import { previewStakeholderImport, confirmStakeholderImport, downloadStakeholderTemplateGeneric } from '../utils/stakeholderService';

// Import API utility functions 
import {
  getUsers,
  createUser,
  updateUser,
  deleteUser,
  getProjects,
  getProject,
  createProject,
  updateProject,
  deleteProject,
  assignUserToProject,
  removeUserFromProject,
  getUserProjects,
  uploadProjectDocument,
  deleteProjectDocument,
  getCustomers,
  createCustomer
} from '../utils/api';

const AdminPanel = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [users, setUsers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [userSort, setUserSort] = useState({ field: 'full_name', dir: 'asc' });
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCustomerId, setFilterCustomerId] = useState('');
  const [filterProjectName, setFilterProjectName] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedProject, setSelectedProject] = useState(null);
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [projectDialogOpen, setProjectDialogOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [userFormData, setUserFormData] = useState({
    email: '',
    full_name: '',
    password: '',
    role: 'user'
  });
  const [projectFormData, setProjectFormData] = useState({
    name: '',
    description: '',
    industry_vertical: '',
    module_details: '',
    implementation_scope: '',
    assigned_users: [],
    customer_id: ''
  });
  const [sowDocument, setSowDocument] = useState(null);
  const [rfpDocument, setRfpDocument] = useState(null);
  const [otherDocuments, setOtherDocuments] = useState([]);
  const [scopeItemsFile, setScopeItemsFile] = useState(null);
  const [stakeholderListFile, setStakeholderListFile] = useState(null);
  const [newCustomerData, setNewCustomerData] = useState({ name: '', address: '', email: '', contact_details: '' });
  const [assignFormData, setAssignFormData] = useState({
    user_id: '',
    project_id: '',
    role: 'member'
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', onConfirm: null });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  // Check if user is admin
  useEffect(() => {
    if (currentUser && !currentUser.role === 'admin') {
      // Not an admin, redirect to home
      navigate('/home');
    }
  }, [currentUser, navigate]);

  // Fetch users, projects and customers on component mount
  useEffect(() => {
    fetchUsers();
    fetchProjects();
    fetchCustomers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await getUsers();
      if (response.success) {
        setUsers(response.users || []);
      } else {
        setError(response.message || 'Failed to fetch users');
        showSnackbar('Failed to fetch users', 'error');
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      setError('An unexpected error occurred while fetching users');
      showSnackbar('Error fetching users', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async () => {
    setLoading(true);
    try {
      const response = await getProjects();
      if (response.success) {
        setProjects(response.projects || []);
      } else {
        setError(response.message || 'Failed to fetch projects');
        showSnackbar('Failed to fetch projects', 'error');
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
      setError('An unexpected error occurred while fetching projects');
      showSnackbar('Error fetching projects', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await getCustomers();
      if (response.success) {
        setCustomers(response.customers || []);
      }
    } catch (err) {
      console.error('Error fetching customers:', err);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleUserDialogOpen = (user = null) => {
    if (user) {
      setSelectedUser(user);
      setUserFormData({
        email: user.email,
        full_name: user.full_name,
        password: '', // Don't populate password for security
        role: user.role
      });
    } else {
      setSelectedUser(null);
      setUserFormData({
        email: '',
        full_name: '',
        password: '',
        role: 'user'
      });
    }
    setUserDialogOpen(true);
  };

  const handleUserDialogClose = () => {
    setUserDialogOpen(false);
    setSelectedUser(null);
    setError('');
  };

  const handleProjectDialogOpen = async (project = null) => {
    if (project) {
      setSelectedProject(project);
      
      // Fetch the latest project data to ensure we have the most up-to-date assigned users
      try {
        const response = await getProject(project.id);
        if (response.success) {
          const currentProject = response.project;
          setSelectedProject(currentProject);
          
          setProjectFormData({
            name: currentProject.name,
            description: currentProject.description || '',
            industry_vertical: currentProject.industry_vertical || '',
            module_details: currentProject.module_details || '',
            implementation_scope: currentProject.implementation_scope || '',
            assigned_users: currentProject.assigned_users?.map(user => user.id) || [],
            customer_id: currentProject.customer_id || ''
          });
          setNewCustomerData({ name: '', address: '', email: '', contact_details: '' });
        } else {
          console.error('Error fetching project details:', response.message);
          // Fallback to the original project data if fetch fails
          setProjectFormData({
            name: project.name,
            description: project.description || '',
            industry_vertical: project.industry_vertical || '',
            module_details: project.module_details || '',
            implementation_scope: project.implementation_scope || '',
            assigned_users: project.assigned_users?.map(user => user.id) || [],
            customer_id: project.customer_id || ''
          });
          setNewCustomerData({ name: '', address: '', email: '', contact_details: '' });
        }
      } catch (error) {
        console.error('Error fetching project details:', error);
        setProjectFormData({
          name: project.name,
          description: project.description || '',
          industry_vertical: project.industry_vertical || '',
          module_details: project.module_details || '',
          implementation_scope: project.implementation_scope || '',
          assigned_users: project.assigned_users?.map(user => user.id) || [],
          customer_id: project.customer_id || ''
        });
        setNewCustomerData({ name: '', address: '', email: '', contact_details: '' });
      }
    } else {
      setSelectedProject(null);
      setProjectFormData({
        name: '',
        description: '',
        industry_vertical: '',
        module_details: '',
        implementation_scope: '',
        assigned_users: [],
        customer_id: ''
      });
      setNewCustomerData({ name: '', address: '', email: '', contact_details: '' });
      // Reset document states for new project
      setSowDocument(null);
      setRfpDocument(null);
      setOtherDocuments([]);
      setScopeItemsFile(null);
      setStakeholderListFile(null);
    }
    setProjectDialogOpen(true);
  };

  const handleProjectDialogClose = () => {
    setProjectDialogOpen(false);
    setSelectedProject(null);
    setSowDocument(null);
    setRfpDocument(null);
    setOtherDocuments([]);
    setScopeItemsFile(null);
    setStakeholderListFile(null);
    setNewCustomerData({ name: '', address: '', email: '', contact_details: '' });
    setError('');
  };

  const handleAssignDialogOpen = () => {
    setAssignFormData({
      user_id: '',
      project_id: '',
      role: 'member'
    });
    setAssignDialogOpen(true);
  };

  const handleAssignDialogClose = () => {
    setAssignDialogOpen(false);
    setError('');
  };

  const handleUserFormChange = (e) => {
    const { name, value } = e.target;
    setUserFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleProjectFormChange = (e) => {
    const { name, value } = e.target;
    setProjectFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleNewCustomerChange = (e) => {
    const { name, value } = e.target;
    setNewCustomerData(prev => ({ ...prev, [name]: value }));
  };

  const handleSowDocumentChange = (e) => {
    setSowDocument(e.target.files[0]);
  };

  const handleRfpDocumentChange = (e) => {
    setRfpDocument(e.target.files[0]);
  };

  const handleOtherDocumentsChange = (e) => {
    setOtherDocuments(Array.from(e.target.files));
  };

  const handleAssignFormChange = (e) => {
    const { name, value } = e.target;
    setAssignFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleUserSubmit = async () => {
    setLoading(true);
    setError('');
    
    try {
      let response;
      
      if (selectedUser) {
        // Update existing user
        const userData = {...userFormData};
        // Only include password if it's provided (for updates)
        if (!userData.password) {
          delete userData.password;
        }
        
        response = await updateUser(selectedUser.id, userData);
        if (response.success) {
          setUsers(users.map(u => u.id === selectedUser.id ? response.user : u));
          showSnackbar('User updated successfully');
        } else {
          setError(response.message || 'Failed to update user');
          showSnackbar('Failed to update user', 'error');
        }
      } else {
        // Create new user
        response = await createUser(userFormData);
        if (response.success) {
          setUsers([...users, response.user]);
          showSnackbar('User created successfully');
        } else {
          setError(response.message || 'Failed to create user');
          showSnackbar('Failed to create user', 'error');
        }
      }
      
      if (response.success) {
        handleUserDialogClose();
      }
    } catch (error) {
      console.error('Error submitting user:', error);
      setError('An unexpected error occurred');
      showSnackbar('An unexpected error occurred', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleProjectSubmit = async () => {
    setLoading(true);
    setError('');
    
    try {
      let response;
      
      if (selectedProject) {
        // Update existing project with file upload support
        if (sowDocument || rfpDocument || otherDocuments.length > 0 || scopeItemsFile || stakeholderListFile) {
          // First update project basic info
          const updateBasicResponse = await updateProject(selectedProject.id, projectFormData);
          
          if (!updateBasicResponse.success) {
            setError(updateBasicResponse.message || 'Failed to update project');
            showSnackbar('Failed to update project basic info', 'error');
            return;
          }
          
          // Then handle document uploads one by one
          let uploadSuccess = true;
          let uploadMessage = '';
          
          if (sowDocument) {
            try {
              showSnackbar('Uploading project document...', 'info');
              const sowUploadResponse = await uploadProjectDocument(sowDocument, selectedProject.id, 'SOW');
              if (sowUploadResponse.success) {
                uploadSuccess = true;
                showSnackbar('Project document uploaded successfully', 'success');
              } else {
                uploadSuccess = false;
                uploadMessage += ' Project document upload failed.';
                showSnackbar('Project document upload failed', 'error');
              }
            } catch (e) {
              uploadSuccess = false;
              uploadMessage += ' Project document upload error.';
              console.error('Error uploading SOW:', e);
              showSnackbar('Error uploading project document', 'error');
            }
          }
          
          if (rfpDocument) {
            try {
              showSnackbar('Uploading RFP document...', 'info');
              const rfpUploadResponse = await uploadProjectDocument(rfpDocument, selectedProject.id, 'RFP');
              if (rfpUploadResponse.success) {
                uploadSuccess = true;
                showSnackbar('RFP document uploaded successfully', 'success');
              } else {
                uploadSuccess = false;
                uploadMessage += ' RFP upload failed.';
                showSnackbar('RFP document upload failed', 'error');
              }
            } catch (e) {
              uploadSuccess = false;
              uploadMessage += ' RFP upload error.';
              console.error('Error uploading RFP:', e);
              showSnackbar('Error uploading RFP document', 'error');
            }
          }
          
          if (otherDocuments.length > 0) {
            showSnackbar(`Uploading ${otherDocuments.length} additional document(s)...`, 'info');
            for (let i = 0; i < otherDocuments.length; i++) {
              try {
                const docUploadResponse = await uploadProjectDocument(otherDocuments[i], selectedProject.id, 'OTHER');
                if (!docUploadResponse.success) {
                  uploadSuccess = false;
                  uploadMessage += ` Document ${i+1} upload failed.`;
                }
              } catch (e) {
                uploadSuccess = false;
                uploadMessage += ` Document ${i+1} upload error.`;
                console.error(`Error uploading document ${i+1}:`, e);
              }
            }
            
            if (uploadSuccess) {
              showSnackbar('Additional documents uploaded successfully', 'success');
            } else {
              showSnackbar('Some document uploads failed', 'warning');
            }
          }
          
          // Handle scope items upload
          const editExtraMessages = [];
          if (scopeItemsFile) {
            try {
              const scopeResult = await uploadScopeItemsExcel(selectedProject.id, scopeItemsFile);
              const count = scopeResult.items?.length || 0;
              const dups = scopeResult.duplicates?.length || 0;
              editExtraMessages.push(`${count} scope item${count !== 1 ? 's' : ''} added${dups > 0 ? ` (${dups} duplicate${dups !== 1 ? 's' : ''} skipped)` : ''}`);
            } catch (e) {
              editExtraMessages.push('Scope items upload failed');
            }
          }
          if (stakeholderListFile) {
            try {
              const preview = await previewStakeholderImport(selectedProject.id, stakeholderListFile);
              if (preview.success) {
                const toImport = (preview.rows || []).filter(r => r.action === 'create' || r.action === 'update');
                if (toImport.length > 0) {
                  await confirmStakeholderImport(selectedProject.id, toImport);
                  const creates = toImport.filter(r => r.action === 'create').length;
                  const updates = toImport.filter(r => r.action === 'update').length;
                  const parts = [];
                  if (creates > 0) parts.push(`${creates} added`);
                  if (updates > 0) parts.push(`${updates} updated`);
                  editExtraMessages.push(`Stakeholders: ${parts.join(', ')}`);
                }
              } else {
                editExtraMessages.push('Stakeholder list upload failed');
              }
            } catch (e) {
              editExtraMessages.push('Stakeholder list upload failed');
            }
          }

          // Get updated project data after all uploads
          response = await getProject(selectedProject.id);
          
          if (response.success) {
            setProjects(projects.map(p => p.id === selectedProject.id ? response.project : p));
            const baseMsg = uploadSuccess ? 'Project updated successfully' : `Project updated but some document uploads failed: ${uploadMessage}`;
            showSnackbar(editExtraMessages.length > 0 ? `${baseMsg}. ${editExtraMessages.join(', ')}.` : baseMsg,
              uploadSuccess ? 'success' : 'warning');
          } else {
            setProjects(projects.map(p => p.id === selectedProject.id ? updateBasicResponse.project : p));
            showSnackbar('Project updated but could not refresh project data', 'warning');
          }
        } else {
          // No files, just update project data normally
          const updateData = { ...projectFormData };
          if (updateData.customer_id === 'new') updateData.customer_id = null;
          response = await updateProject(selectedProject.id, updateData);
          if (response.success) {
            setProjects(projects.map(p => p.id === selectedProject.id ? response.project : p));
            showSnackbar('Project updated successfully');
          } else {
            setError(response.message || 'Failed to update project');
            showSnackbar('Failed to update project', 'error');
          }
        }
      } else {
        // Create new project with files
        // If creating a new customer inline, do that first
        let resolvedCustomerId = projectFormData.customer_id;
        if (resolvedCustomerId === 'new') {
          if (!newCustomerData.name.trim()) {
            setError('Customer Name is required when creating a new customer');
            setLoading(false);
            return;
          }
          const customerRes = await createCustomer(newCustomerData);
          if (!customerRes.success) {
            setError(customerRes.message || 'Failed to create customer');
            showSnackbar('Failed to create customer', 'error');
            setLoading(false);
            return;
          }
          resolvedCustomerId = customerRes.customer.id;
          setCustomers(prev => [...prev, customerRes.customer]);
        }

        const projectFormDataObj = new FormData();
        
        // Add project details
        Object.keys(projectFormData).forEach(key => {
          if (key === 'assigned_users' && projectFormData[key].length > 0) {
            projectFormDataObj.append('assigned_users', JSON.stringify(projectFormData[key]));
          } else if (key === 'customer_id') {
            if (resolvedCustomerId) projectFormDataObj.append('customer_id', resolvedCustomerId);
          } else {
            projectFormDataObj.append(key, projectFormData[key]);
          }
        });
        
        // Add SOW document if selected
        if (sowDocument) {
          projectFormDataObj.append('sow_document', sowDocument);
        }
        
        // Add RFP document if selected
        if (rfpDocument) {
          projectFormDataObj.append('rfp_document', rfpDocument);
        }
        
        // Add other documents if selected
        if (otherDocuments.length > 0) {
          otherDocuments.forEach(doc => {
            projectFormDataObj.append('other_documents', doc);
          });
        }
        
        response = await createProject(projectFormDataObj);
        if (response.success) {
          const newProjectId = response.project.id;
          const extraMessages = [];

          if (scopeItemsFile) {
            try {
              const scopeResult = await uploadScopeItemsExcel(newProjectId, scopeItemsFile);
              const count = scopeResult.items?.length || 0;
              const dups = scopeResult.duplicates?.length || 0;
              extraMessages.push(`${count} scope item${count !== 1 ? 's' : ''} imported${dups > 0 ? ` (${dups} duplicate${dups !== 1 ? 's' : ''} skipped)` : ''}`);
            } catch (e) {
              extraMessages.push('Scope items upload failed');
            }
          }

          if (stakeholderListFile) {
            try {
              const preview = await previewStakeholderImport(newProjectId, stakeholderListFile);
              if (preview.success) {
                const toImport = (preview.rows || []).filter(r => r.action === 'create' || r.action === 'update');
                if (toImport.length > 0) {
                  await confirmStakeholderImport(newProjectId, toImport);
                  extraMessages.push(`${toImport.length} stakeholder${toImport.length !== 1 ? 's' : ''} imported`);
                } else {
                  extraMessages.push('No new stakeholders to import');
                }
              } else {
                extraMessages.push('Stakeholder list upload failed');
              }
            } catch (e) {
              extraMessages.push('Stakeholder list upload failed');
            }
          }

          setProjects([...projects, response.project]);
          showSnackbar(extraMessages.length > 0
            ? `Project created. ${extraMessages.join(', ')}.`
            : 'Project created successfully');
        } else {
          setError(response.message || 'Failed to create project');
          showSnackbar('Failed to create project', 'error');
        }
      }
      
      if (response && response.success) {
        // Reset document states
        setSowDocument(null);
        setRfpDocument(null);
        setOtherDocuments([]);
        setScopeItemsFile(null);
        setStakeholderListFile(null);
        handleProjectDialogClose();
        
        // Refresh the projects list to get the latest data
        fetchProjects();
      }
    } catch (error) {
      console.error('Error submitting project:', error);
      setError('An unexpected error occurred');
      showSnackbar('An unexpected error occurred', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleAssignSubmit = async () => {
    setLoading(true);
    setError('');
    
    try {
      console.log('Submitting assignment:', {
        projectId: assignFormData.project_id,
        userData: {
          user_id: assignFormData.user_id,
          role: assignFormData.role
        }
      });
      
      const response = await assignUserToProject(
        assignFormData.project_id,
        {
          user_id: assignFormData.user_id,
          role: assignFormData.role
        }
      );
      
      console.log('Assignment response:', response);
      
      if (response.success) {
        // Refresh projects to show updated assignments
        fetchProjects();
        showSnackbar('User assigned to project successfully');
        handleAssignDialogClose();
      } else {
        setError(response.message || 'Failed to assign user to project');
        showSnackbar('Failed to assign user to project', 'error');
      }
    } catch (error) {
      console.error('Error assigning user to project:', error);
      setError('An unexpected error occurred');
      showSnackbar('An unexpected error occurred', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteConfirm = (item, type) => {
    setItemToDelete({ item, type });
    setDeleteConfirmOpen(true);
  };

  const handleDelete = async () => {
    if (!itemToDelete) return;
    
    setLoading(true);
    
    try {
      const { item, type } = itemToDelete;
      let response;
      
      if (type === 'user') {
        response = await deleteUser(item.id);
        if (response.success) {
          setUsers(users.filter(u => u.id !== item.id));
          showSnackbar('User deleted successfully');
        } else {
          setError(response.message || 'Failed to delete user');
          showSnackbar('Failed to delete user', 'error');
        }
      } else if (type === 'project') {
        response = await deleteProject(item.id);
        if (response.success) {
          setProjects(projects.filter(p => p.id !== item.id));
          showSnackbar('Project deleted successfully');
        } else {
          setError(response.message || 'Failed to delete project');
          showSnackbar('Failed to delete project', 'error');
        }
      } else if (type === 'assignment') {
        console.log('Removing user from project:', item);
        response = await removeUserFromProject(item.project_id, item.user_id);
        if (response.success) {
          // Refresh projects to show updated assignments
          fetchProjects();
          showSnackbar('User removed from project successfully');
        } else {
          setError(response.message || 'Failed to remove user from project');
          showSnackbar('Failed to remove user from project', 'error');
        }
      }
    } catch (error) {
      console.error('Error deleting item:', error);
      setError('An unexpected error occurred');
      showSnackbar('An unexpected error occurred', 'error');
    } finally {
      setLoading(false);
      setDeleteConfirmOpen(false);
      setItemToDelete(null);
    }
  };

  const handleRemoveUserFromProject = (projectId, userId) => {
    console.log(`Removing user ${userId} from project ${projectId}`);
    handleDeleteConfirm({ project_id: projectId, user_id: userId }, 'assignment');
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Get initials for avatar
  const getInitials = (name) => {
    if (!name) return '?';
    return name
      .split(' ')
      .map(part => part[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Get role display name and color
  const getRoleDisplay = (role) => {
    const roleMap = {
      'admin': { label: 'Administrator', color: theme.palette.error.main },
      'manager': { label: 'Manager', color: theme.palette.warning.main },
      'user': { label: 'User', color: theme.palette.info.main }
    };
    
    return roleMap[role] || { label: role, color: theme.palette.grey[500] };
  };

  // Filtered + sorted user list
  const filteredUsers = useMemo(() => {
    const q = userSearch.toLowerCase();
    const filtered = q
      ? users.filter(u =>
          u.full_name.toLowerCase().includes(q) ||
          u.email.toLowerCase().includes(q)
        )
      : users;
    return [...filtered].sort((a, b) => {
      const av = (a[userSort.field] || '').toLowerCase();
      const bv = (b[userSort.field] || '').toLowerCase();
      return userSort.dir === 'asc' ? av.localeCompare(bv) : bv.localeCompare(av);
    });
  }, [users, userSearch, userSort]);

  const handleUserSort = (field) => {
    setUserSort(prev =>
      prev.field === field
        ? { field, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { field, dir: 'asc' }
    );
  };

  // Filtered project list for the admin projects tab
  const filteredProjects = useMemo(() => {
    return projects.filter(project => {
      if (filterCustomerId && String(project.customer_id) !== String(filterCustomerId)) return false;
      if (filterProjectName && project.name !== filterProjectName) return false;
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const allDocs = [
          ...(project.documents?.sow || []),
          ...(project.documents?.rfp || []),
          ...(project.documents?.functional_specs || []),
          ...(project.documents?.technical_specs || []),
          ...(project.documents?.requirements || []),
          ...(project.documents?.other || []),
        ];
        const docMatch = allDocs.some(d => (d.title || '').toLowerCase().includes(q));
        const textMatch =
          project.name.toLowerCase().includes(q) ||
          (project.customer_name || '').toLowerCase().includes(q) ||
          (project.description || '').toLowerCase().includes(q) ||
          docMatch;
        if (!textMatch) return false;
      }
      return true;
    });
  }, [projects, searchQuery, filterCustomerId, filterProjectName]);

  const hasActiveFilters = searchQuery || filterCustomerId || filterProjectName;

  const clearAllFilters = () => {
    setSearchQuery('');
    setFilterCustomerId('');
    setFilterProjectName('');
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper 
        elevation={3} 
        sx={{ 
          p: 3, 
          mb: 4,
          background: 'linear-gradient(to right, #1D2B3F, #283a53)',
          borderRadius: 2,
          boxShadow: '0 8px 16px rgba(0, 0, 0, 0.4)'
        }}
      >
        <Typography variant="h4" fontWeight="bold" color="white" gutterBottom>
          Admin Panel
        </Typography>
        <Typography variant="subtitle1" color="rgba(255, 255, 255, 0.7)">
          Manage users, projects, and assignments
        </Typography>
      </Paper>

      <Box sx={{ mb: 3 }}>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange}
          sx={{
            '& .Mui-selected': {
              color: theme.palette.primary.main
            }
          }}
        >
          <Tab 
            label="Users" 
            icon={<PersonIcon />} 
            iconPosition="start"
          />
          <Tab 
            label="Projects" 
            icon={<BusinessIcon />} 
            iconPosition="start"
          />
        </Tabs>
      </Box>

      {/* Users Tab */}
      {tabValue === 0 && (
        <Box>
          {/* Header row */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h5" fontWeight="500">
              User Management
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<PersonAddIcon />}
              onClick={() => handleUserDialogOpen()}
            >
              Add New User
            </Button>
          </Box>

          {/* Search bar */}
          <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
            <TextField
              placeholder="Search by name or email…"
              value={userSearch}
              onChange={e => setUserSearch(e.target.value)}
              size="small"
              sx={{ width: 320 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
                endAdornment: userSearch ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setUserSearch('')}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null
              }}
            />
            <Typography variant="caption" color="text.secondary">
              {filteredUsers.length} of {users.length} user{users.length !== 1 ? 's' : ''}
            </Typography>
          </Box>

          {loading && users.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : filteredUsers.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <SearchIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
              <Typography color="text.secondary">No users match your search.</Typography>
            </Box>
          ) : (
            <TableContainer
              component={Paper}
              sx={{
                background: 'linear-gradient(135deg, #1e1e1e 0%, #252525 100%)',
                border: '1px solid rgba(255,255,255,0.07)',
                borderRadius: 2,
              }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ '& th': { borderColor: 'rgba(255,255,255,0.08)', fontWeight: 600, whiteSpace: 'nowrap' } }}>
                    <TableCell sx={{ width: 48 }} />
                    <TableCell>
                      <TableSortLabel
                        active={userSort.field === 'full_name'}
                        direction={userSort.field === 'full_name' ? userSort.dir : 'asc'}
                        onClick={() => handleUserSort('full_name')}
                      >
                        Name
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={userSort.field === 'email'}
                        direction={userSort.field === 'email' ? userSort.dir : 'asc'}
                        onClick={() => handleUserSort('email')}
                      >
                        Email
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={userSort.field === 'role'}
                        direction={userSort.field === 'role' ? userSort.dir : 'asc'}
                        onClick={() => handleUserSort('role')}
                      >
                        Role
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={userSort.field === 'created_at'}
                        direction={userSort.field === 'created_at' ? userSort.dir : 'asc'}
                        onClick={() => handleUserSort('created_at')}
                      >
                        Joined
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredUsers.map(user => (
                    <TableRow
                      key={user.id}
                      hover
                      sx={{
                        '& td': { borderColor: 'rgba(255,255,255,0.05)' },
                        '&:last-child td': { border: 0 },
                        '&:hover': { bgcolor: 'rgba(255,255,255,0.03)' }
                      }}
                    >
                      {/* Avatar */}
                      <TableCell>
                        <Avatar
                          sx={{
                            bgcolor: getRoleDisplay(user.role).color,
                            width: 34,
                            height: 34,
                            fontSize: '0.8rem'
                          }}
                        >
                          {getInitials(user.full_name)}
                        </Avatar>
                      </TableCell>

                      {/* Name */}
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {user.full_name}
                        </Typography>
                      </TableCell>

                      {/* Email */}
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {user.email}
                        </Typography>
                      </TableCell>

                      {/* Role chip */}
                      <TableCell>
                        <Chip
                          label={getRoleDisplay(user.role).label}
                          size="small"
                          sx={{
                            bgcolor: alpha(getRoleDisplay(user.role).color, 0.12),
                            color: getRoleDisplay(user.role).color,
                            fontWeight: 500,
                            fontSize: '0.7rem'
                          }}
                        />
                      </TableCell>

                      {/* Joined */}
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(user.created_at)}
                        </Typography>
                      </TableCell>

                      {/* Actions */}
                      <TableCell align="right">
                        <Tooltip title="Edit user">
                          <IconButton size="small" onClick={() => handleUserDialogOpen(user)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete user">
                          <IconButton size="small" color="error" onClick={() => handleDeleteConfirm(user, 'user')}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      )}

      {/* Projects Tab */}
      {tabValue === 1 && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h5" fontWeight="500">
              Project Management
            </Typography>
            <Box>
              <Button
                variant="outlined"
                color="primary"
                startIcon={<GroupAddIcon />}
                onClick={handleAssignDialogOpen}
                sx={{ mr: 2 }}
              >
                Assign User
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => handleProjectDialogOpen()}
              >
                Add New Project
              </Button>
            </Box>
          </Box>

          {/* Search & filter bar */}
          <Box sx={{ mb: 3, display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
            <TextField
              placeholder="Search by project, customer, or document name…"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              size="small"
              sx={{ flex: '1 1 280px', minWidth: 220 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
                endAdornment: searchQuery ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchQuery('')}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null
              }}
            />
            <FormControl size="small" sx={{ minWidth: 180 }}>
              <InputLabel>Customer</InputLabel>
              <Select
                value={filterCustomerId}
                onChange={e => setFilterCustomerId(e.target.value)}
                label="Customer"
              >
                <MenuItem value=""><em>All Customers</em></MenuItem>
                {customers.map(c => (
                  <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel>Project</InputLabel>
              <Select
                value={filterProjectName}
                onChange={e => setFilterProjectName(e.target.value)}
                label="Project"
              >
                <MenuItem value=""><em>All Projects</em></MenuItem>
                {[...new Set(projects.map(p => p.name))].sort().map(name => (
                  <MenuItem key={name} value={name}>{name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {hasActiveFilters && (
              <Tooltip title="Clear all filters">
                <Button
                  size="small"
                  variant="outlined"
                  color="secondary"
                  startIcon={<ClearIcon />}
                  onClick={clearAllFilters}
                >
                  Clear
                </Button>
              </Tooltip>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
              {filteredProjects.length} of {projects.length} project{projects.length !== 1 ? 's' : ''}
            </Typography>
          </Box>

          {loading && projects.length === 0 ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : filteredProjects.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <SearchIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
              <Typography color="text.secondary">
                No projects match your search{hasActiveFilters ? ' — try adjusting filters' : ''}.
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={3}>
              {filteredProjects.map(project => (
                <Grid item xs={12} key={project.id}>
                  <Card 
                    sx={{ 
                      background: 'linear-gradient(135deg, #1e1e1e 0%, #252525 100%)',
                      border: '1px solid rgba(255, 255, 255, 0.05)',
                      mb: 3,
                      transition: 'all 0.2s',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 6px 20px rgba(0, 0, 0, 0.2)',
                        border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                      }
                    }}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                        <Typography variant="h5" fontWeight="500">
                          {project.name}
                        </Typography>
                        <Box>
                          <IconButton 
                            size="small" 
                            color="primary"
                            onClick={() => handleProjectDialogOpen(project)}
                            sx={{ mr: 1 }}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton 
                            size="small" 
                            color="error"
                            onClick={() => handleDeleteConfirm(project, 'project')}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      </Box>
                      
                      <Typography variant="body2" paragraph>
                        {project.description || 'No description provided'}
                      </Typography>

                      {project.customer_name && (
                        <Box sx={{ mb: 1 }}>
                          <Typography variant="caption" color="text.secondary">Customer: </Typography>
                          <Typography variant="caption" fontWeight="600">{project.customer_name}</Typography>
                        </Box>
                      )}

                      <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={12} md={4}>
                          <Typography variant="subtitle2" fontWeight="500" gutterBottom>
                            Industry
                          </Typography>
                          <Typography variant="body2">
                            {project.industry_vertical || 'Not specified'}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Typography variant="subtitle2" fontWeight="500" gutterBottom>
                            Modules
                          </Typography>
                          <Typography variant="body2">
                            {project.module_details || 'Not specified'}
                          </Typography>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Typography variant="subtitle2" fontWeight="500" gutterBottom>
                            Implementation
                          </Typography>
                          <Typography variant="body2">
                            {project.implementation_scope || 'Not specified'}
                          </Typography>
                        </Grid>
                      </Grid>
                      
                      <Divider sx={{ my: 2 }} />
                      
                      <Typography variant="subtitle1" fontWeight="500" gutterBottom>
                        Assigned Users
                      </Typography>
                      
                      {project.assigned_users && project.assigned_users.length > 0 ? (
                        <List>
                          {project.assigned_users.map(user => (
                            <ListItem key={user.id}>
                              <ListItemAvatar>
                                <Avatar
                                  sx={{ 
                                    bgcolor: getRoleDisplay(user.role).color 
                                  }}
                                >
                                  {getInitials(user.full_name)}
                                </Avatar>
                              </ListItemAvatar>
                              <ListItemText
                                primary={user.full_name}
                                secondary={`${user.email} • ${getRoleDisplay(user.role).label}`}
                              />
                              <ListItemSecondaryAction>
                                <IconButton
                                  edge="end"
                                  color="error"
                                  onClick={() => handleRemoveUserFromProject(project.id, user.id)}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </ListItemSecondaryAction>
                            </ListItem>
                          ))}
                        </List>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No users assigned to this project yet.
                        </Typography>
                      )}
                      
                      <Divider sx={{ my: 2 }} />
                      
                      <Typography variant="subtitle1" fontWeight="500" gutterBottom>
                        Project Documents & AI Insights
                      </Typography>
                      
                      {project.documents ? (
                        <>
                          {/* SOW Documents */}
                          {project.documents.sow && project.documents.sow.length > 0 && (
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="subtitle2" gutterBottom>
                                Project Documents
                              </Typography>
                              <List dense>
                                {project.documents.sow.map((doc) => (
                                  <ListItem key={doc.id || doc.document_id}>
                                    <ListItemIcon>
                                      <BusinessIcon color="primary" />
                                    </ListItemIcon>
                                    <ListItemText
                                      primary={doc.title}
                                      secondary={`Project Document${doc.created_at ? ` • Uploaded ${formatDate(doc.created_at)}` : ''}`}
                                    />
                                  </ListItem>
                                ))}
                              </List>
                            </Box>
                          )}
                          
                          {/* RFP Documents */}
                          {project.documents.rfp && project.documents.rfp.length > 0 && (
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="subtitle2" gutterBottom>
                                Request for Proposal
                              </Typography>
                              <List dense>
                                {project.documents.rfp.map((doc) => (
                                  <ListItem key={doc.id || doc.document_id}>
                                    <ListItemIcon>
                                      <DescriptionIcon color="secondary" />
                                    </ListItemIcon>
                                    <ListItemText
                                      primary={doc.title}
                                      secondary={`RFP${doc.created_at ? ` • Uploaded ${formatDate(doc.created_at)}` : ''}`}
                                    />
                                  </ListItem>
                                ))}
                              </List>
                            </Box>
                          )}
                          
                          {/* Functional Specifications */}
                          {project.documents.functional_specs && project.documents.functional_specs.length > 0 && (
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="subtitle2" gutterBottom>
                                Functional Specifications
                              </Typography>
                              <List dense>
                                {project.documents.functional_specs.map((doc) => (
                                  <ListItem key={doc.id || doc.document_id}>
                                    <ListItemIcon>
                                      <DescriptionIcon color="secondary" />
                                    </ListItemIcon>
                                    <ListItemText
                                      primary={doc.title}
                                      secondary={`Functional Spec${doc.created_at ? ` • Uploaded ${formatDate(doc.created_at)}` : ''}`}
                                    />
                                  </ListItem>
                                ))}
                              </List>
                            </Box>
                          )}
                          
                          {/* Other Documents */}
                          {project.documents.other && project.documents.other.length > 0 && (
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="subtitle2" gutterBottom>
                                Other Documents
                              </Typography>
                              <List dense>
                                {project.documents.other.map((doc) => (
                                  <ListItem key={doc.id || doc.document_id}>
                                    <ListItemIcon>
                                      <DescriptionIcon />
                                    </ListItemIcon>
                                    <ListItemText
                                      primary={doc.title}
                                      secondary={`${doc.document_type || "Document"}${doc.created_at ? ` • Uploaded ${formatDate(doc.created_at)}` : ''}`}
                                    />
                                  </ListItem>
                                ))}
                              </List>
                            </Box>
                          )}
                          
                          {project.ai_insights && (
                            <Box sx={{ mb: 2 }}>
                              <Typography variant="subtitle2" gutterBottom>
                                AI Insights
                              </Typography>
                              <Paper 
                                variant="outlined" 
                                sx={{ 
                                  p: 2, 
                                  bgcolor: alpha(theme.palette.primary.main, 0.05),
                                  borderColor: alpha(theme.palette.primary.main, 0.2) 
                                }}
                              >
                                <Typography variant="body2" gutterBottom>
                                  <strong>Key Objectives:</strong> {project.ai_insights?.key_objectives || "Not yet extracted"}
                                </Typography>
                                <Typography variant="body2" gutterBottom>
                                  <strong>Project Timeline:</strong> {project.ai_insights?.project_timeline || "Not yet extracted"}
                                </Typography>
                                <Typography variant="body2" gutterBottom>
                                  <strong>Key Requirements:</strong> {project.ai_insights?.key_requirements || "Not yet extracted"}
                                </Typography>
                                <Typography variant="body2">
                                  <strong>Other Insights:</strong> {project.ai_insights?.additional_insights || "No additional insights available"}
                                </Typography>
                              </Paper>
                            </Box>
                          )}
                        </>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          No documents uploaded to this project yet. Upload documents to enable AI insights.
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      )}

      {/* User Dialog */}
      <Dialog 
        open={userDialogOpen} 
        onClose={handleUserDialogClose}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {selectedUser ? 'Edit User' : 'Add New User'}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          <TextField
            label="Email Address"
            name="email"
            value={userFormData.email}
            onChange={handleUserFormChange}
            fullWidth
            margin="normal"
            required
          />
          
          <TextField
            label="Full Name"
            name="full_name"
            value={userFormData.full_name}
            onChange={handleUserFormChange}
            fullWidth
            margin="normal"
            required
          />
          
          <TextField
            label="Password"
            name="password"
            type="password"
            value={userFormData.password}
            onChange={handleUserFormChange}
            fullWidth
            margin="normal"
            required={!selectedUser}
            helperText={selectedUser ? "Leave blank to keep current password" : "Password must meet security requirements"}
          />
          
          <FormControl fullWidth margin="normal">
            <InputLabel>Role</InputLabel>
            <Select
              name="role"
              value={userFormData.role}
              onChange={handleUserFormChange}
              label="Role"
            >
              <MenuItem value="admin">Administrator</MenuItem>
              <MenuItem value="manager">Manager</MenuItem>
              <MenuItem value="user">Regular User</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleUserDialogClose}>Cancel</Button>
          <Button 
            onClick={handleUserSubmit} 
            color="primary" 
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : (selectedUser ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Project Dialog */}
      <Dialog 
        open={projectDialogOpen} 
        onClose={handleProjectDialogClose}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          {selectedProject ? 'Edit Project' : 'Add New Project'}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          <FormControl fullWidth margin="normal">
            <InputLabel>Customer</InputLabel>
            <Select
              name="customer_id"
              value={projectFormData.customer_id || ''}
              onChange={handleProjectFormChange}
              label="Customer"
            >
              <MenuItem value=""><em>No customer assigned</em></MenuItem>
              {customers.map((c) => (
                <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
              ))}
              <MenuItem value="new" sx={{ color: 'primary.main', fontWeight: 600 }}>+ Create New Customer</MenuItem>
            </Select>
          </FormControl>

          {/* Inline new customer form */}
          {projectFormData.customer_id === 'new' && (
            <Box sx={{ mt: 1, mb: 1, p: 2, border: '1px solid', borderColor: 'primary.main', borderRadius: 1, bgcolor: 'rgba(25, 118, 210, 0.05)' }}>
              <Typography variant="subtitle2" color="primary" gutterBottom>
                New Customer Details
              </Typography>
              <TextField
                label="Customer Name"
                name="name"
                value={newCustomerData.name}
                onChange={handleNewCustomerChange}
                fullWidth
                margin="dense"
                required
                placeholder="Required"
              />
              <TextField
                label="Customer Address"
                name="address"
                value={newCustomerData.address}
                onChange={handleNewCustomerChange}
                fullWidth
                margin="dense"
                multiline
                rows={2}
              />
              <TextField
                label="Customer Email Address"
                name="email"
                value={newCustomerData.email}
                onChange={handleNewCustomerChange}
                fullWidth
                margin="dense"
                type="email"
              />
              <TextField
                label="Customer Contact Details"
                name="contact_details"
                value={newCustomerData.contact_details}
                onChange={handleNewCustomerChange}
                fullWidth
                margin="dense"
                multiline
                rows={2}
                placeholder="Phone number, contact person, etc."
              />
            </Box>
          )}

          <TextField
            label="Project Name"
            name="name"
            value={projectFormData.name}
            onChange={handleProjectFormChange}
            fullWidth
            margin="normal"
            required
          />
          
          <TextField
            label="Description"
            name="description"
            value={projectFormData.description}
            onChange={handleProjectFormChange}
            fullWidth
            margin="normal"
            multiline
            rows={3}
          />
          
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                label="Industry Vertical"
                name="industry_vertical"
                value={projectFormData.industry_vertical}
                onChange={handleProjectFormChange}
                fullWidth
                margin="normal"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Module Details"
                name="module_details"
                value={projectFormData.module_details}
                onChange={handleProjectFormChange}
                fullWidth
                margin="normal"
                helperText="Comma-separated list"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Implementation Scope"
                name="implementation_scope"
                value={projectFormData.implementation_scope}
                onChange={handleProjectFormChange}
                fullWidth
                margin="normal"
                select
                SelectProps={{
                  native: true
                }}
              >
                <option value=""></option>
                <option value="Full implementation">Full implementation</option>
                <option value="Enhancement">Enhancement</option>
                <option value="Migration">Migration</option>
                <option value="Integration">Integration</option>
                <option value="Support">Support</option>
              </TextField>
            </Grid>
          </Grid>
          
          <FormControl fullWidth margin="normal">
            <InputLabel>Assigned Users</InputLabel>
            <Select
              multiple
              name="assigned_users"
              value={projectFormData.assigned_users}
              onChange={handleProjectFormChange}
              label="Assigned Users"
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((userId) => {
                    const user = users.find(u => u.id === userId);
                    return user ? (
                      <Chip 
                        key={userId} 
                        label={user.full_name} 
                        size="small"
                      />
                    ) : null;
                  })}
                </Box>
              )}
            >
              {users.map((user) => (
                <MenuItem key={user.id} value={user.id}>
                  {user.full_name} ({user.email})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          {/* Existing documents section — only shown when editing */}
          {selectedProject && (
            <>
              <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
                Existing Documents
              </Typography>

              {/* Existing SOWs */}
              {selectedProject.documents?.sow?.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>Project Documents</Typography>
                  {selectedProject.documents.sow.map((doc) => (
                    <Box key={doc.document_id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <DescriptionIcon fontSize="small" color="primary" />
                        <Typography variant="body2">{doc.title}</Typography>
                      </Box>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => setConfirmDialog({
                          open: true,
                          title: 'Delete Project Document',
                          message: `Are you sure you want to permanently delete "${doc.title}"? This cannot be undone.`,
                          onConfirm: async () => {
                            const res = await deleteProjectDocument(selectedProject.id, doc.document_id);
                            if (res.success) {
                              showSnackbar('Project document deleted successfully', 'success');
                              const updated = await getProject(selectedProject.id);
                              if (updated.success) setSelectedProject(updated.project);
                            } else {
                              showSnackbar(res.error || 'Failed to delete', 'error');
                            }
                          }
                        })}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              )}

              {/* Existing RFPs */}
              {selectedProject.documents?.rfp?.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>Request for Proposal (RFP)</Typography>
                  {selectedProject.documents.rfp.map((doc) => (
                    <Box key={doc.document_id} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1, mb: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <DescriptionIcon fontSize="small" color="secondary" />
                        <Typography variant="body2">{doc.title}</Typography>
                      </Box>
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => setConfirmDialog({
                          open: true,
                          title: 'Delete RFP Document',
                          message: `Are you sure you want to permanently delete "${doc.title}"? This cannot be undone.`,
                          onConfirm: async () => {
                            const res = await deleteProjectDocument(selectedProject.id, doc.document_id);
                            if (res.success) {
                              showSnackbar('RFP deleted successfully', 'success');
                              const updated = await getProject(selectedProject.id);
                              if (updated.success) setSelectedProject(updated.project);
                            } else {
                              showSnackbar(res.error || 'Failed to delete', 'error');
                            }
                          }
                        })}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  ))}
                </Box>
              )}
            </>
          )}

          {/* Document upload section for both new and edit project */}
          <Typography variant="subtitle1" sx={{ mt: 2, mb: 1 }}>
            {selectedProject ? 'Upload Additional Documents' : 'Upload Project Documents'}
          </Typography>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Project Documents - Optional
            </Typography>
            <input
              accept=".pdf,.docx,.doc,.txt,.md"
              id="sow-document-upload"
              type="file"
              onChange={handleSowDocumentChange}
              style={{ display: 'none' }}
            />
            <label htmlFor="sow-document-upload">
              <Button
                variant="outlined"
                component="span"
                fullWidth
                sx={{ mb: 1 }}
              >
                {sowDocument ? sowDocument.name : 'Upload Project Documents (Optional)'}
              </Button>
            </label>
            {sowDocument && (
              <Typography variant="caption" display="block" gutterBottom>
                Selected: {sowDocument.name} ({Math.round(sowDocument.size/1024)} KB)
              </Typography>
            )}
          </Box>
          
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Request for Proposal (RFP) - Optional
            </Typography>
            <input
              accept=".pdf,.docx,.doc,.txt,.md"
              id="rfp-document-upload"
              type="file"
              onChange={handleRfpDocumentChange}
              style={{ display: 'none' }}
            />
            <label htmlFor="rfp-document-upload">
              <Button
                variant="outlined"
                component="span"
                fullWidth
                sx={{ mb: 1 }}
              >
                {rfpDocument ? rfpDocument.name : 'Upload RFP Document (Optional)'}
              </Button>
            </label>
            {rfpDocument && (
              <Typography variant="caption" display="block" gutterBottom>
                Selected: {rfpDocument.name} ({Math.round(rfpDocument.size/1024)} KB)
              </Typography>
            )}
          </Box>
          
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" gutterBottom>
              Additional Documents (Optional)
            </Typography>
            <input
              accept=".pdf,.docx,.doc,.txt,.md"
              id="other-documents-upload"
              type="file"
              multiple
              onChange={handleOtherDocumentsChange}
              style={{ display: 'none' }}
            />
            <label htmlFor="other-documents-upload">
              <Button
                variant="outlined"
                component="span"
                fullWidth
              >
                Upload Additional Documents
              </Button>
            </label>
            {otherDocuments.length > 0 && (
              <Typography variant="caption" display="block" gutterBottom>
                {otherDocuments.length} file(s) selected
              </Typography>
            )}
            {otherDocuments.length > 0 && (
              <Box sx={{ mt: 1, maxHeight: '100px', overflowY: 'auto' }}>
                {otherDocuments.map((doc, index) => (
                  <Typography key={index} variant="caption" display="block">
                    {doc.name} ({Math.round(doc.size/1024)} KB)
                  </Typography>
                ))}
              </Box>
            )}
          </Box>

          {/* ── Scope Items Upload ────────────────────────────────────────── */}
          <Box sx={{ mt: 3, mb: 1 }}>
            <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <TableChartIcon fontSize="small" color="action" />
              Scope Items (Optional)
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              Upload an Excel file to pre-populate workshop scope items. Use the same template as Workshop Schedule → Step 2.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<GetAppIcon />}
                onClick={() => downloadScopeItemTemplate()}
              >
                Download Template
              </Button>
              <input
                accept=".xlsx,.xls"
                id="scope-items-upload"
                type="file"
                onChange={e => setScopeItemsFile(e.target.files[0] || null)}
                style={{ display: 'none' }}
              />
              <label htmlFor="scope-items-upload">
                <Button size="small" variant="outlined" component="span" startIcon={<CloudUploadIcon />}
                  color={scopeItemsFile ? 'success' : 'primary'}>
                  {scopeItemsFile ? scopeItemsFile.name : 'Upload Scope Items'}
                </Button>
              </label>
              {scopeItemsFile && (
                <Button size="small" color="error" onClick={() => setScopeItemsFile(null)}>Clear</Button>
              )}
            </Box>
          </Box>

          {/* ── Stakeholder List Upload ───────────────────────────────────── */}
          <Box sx={{ mt: 2, mb: 2 }}>
            <Typography variant="subtitle1" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <PeopleAltIcon fontSize="small" color="action" />
              Stakeholder List (Optional)
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              Upload an Excel file to pre-populate the project team. Use the same template as Project Overview → Project Team.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
              <Button
                size="small"
                variant="outlined"
                startIcon={<GetAppIcon />}
                onClick={() => downloadStakeholderTemplateGeneric()}
              >
                Download Template
              </Button>
              <input
                accept=".xlsx,.xls"
                id="stakeholder-list-upload"
                type="file"
                onChange={e => setStakeholderListFile(e.target.files[0] || null)}
                style={{ display: 'none' }}
              />
              <label htmlFor="stakeholder-list-upload">
                <Button size="small" variant="outlined" component="span" startIcon={<CloudUploadIcon />}
                  color={stakeholderListFile ? 'success' : 'primary'}>
                  {stakeholderListFile ? stakeholderListFile.name : 'Upload Stakeholder List'}
                </Button>
              </label>
              {stakeholderListFile && (
                <Button size="small" color="error" onClick={() => setStakeholderListFile(null)}>Clear</Button>
              )}
            </Box>
          </Box>
          
          {selectedProject && selectedProject.documents && (
            <Box sx={{ mt: 2, mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Current Project Documents
              </Typography>
              <Paper variant="outlined" sx={{ p: 2 }}>
                {/* SOW Documents */}
                {selectedProject.documents.sow && selectedProject.documents.sow.length > 0 ? (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="primary">
                      Project Documents
                    </Typography>
                    <List dense>
                      {selectedProject.documents.sow.map((doc) => (
                        <ListItem key={doc.id || doc.document_id}>
                          <ListItemIcon>
                            <BusinessIcon color="primary" />
                          </ListItemIcon>
                          <ListItemText
                            primary={doc.title}
                            secondary={`Uploaded on ${doc.created_at ? new Date(doc.created_at).toLocaleDateString() : 'N/A'}`}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No project documents uploaded yet
                  </Typography>
                )}
                
                {/* RFP Documents */}
                {selectedProject.documents.rfp && selectedProject.documents.rfp.length > 0 ? (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="primary">
                      Request for Proposal
                    </Typography>
                    <List dense>
                      {selectedProject.documents.rfp.map((doc) => (
                        <ListItem key={doc.id || doc.document_id}>
                          <ListItemIcon>
                            <DescriptionIcon color="primary" />
                          </ListItemIcon>
                          <ListItemText
                            primary={doc.title}
                            secondary={`Uploaded on ${doc.created_at ? new Date(doc.created_at).toLocaleDateString() : 'N/A'}`}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No RFP document uploaded yet
                  </Typography>
                )}
                
                {/* Functional Specifications */}
                {selectedProject.documents.functional_specs && selectedProject.documents.functional_specs.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle2" color="primary" sx={{ mt: 1 }}>
                      Functional Specifications
                    </Typography>
                    <List dense>
                      {selectedProject.documents.functional_specs.map((doc) => (
                        <ListItem key={doc.id || doc.document_id}>
                          <ListItemIcon>
                            <DescriptionIcon color="secondary" />
                          </ListItemIcon>
                          <ListItemText
                            primary={doc.title}
                            secondary={`Uploaded on ${doc.created_at ? new Date(doc.created_at).toLocaleDateString() : 'N/A'}`}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}
                
                {/* Other Documents */}
                {selectedProject.documents.other && selectedProject.documents.other.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" color="primary" sx={{ mt: 1 }}>
                      Other Documents
                    </Typography>
                    <List dense>
                      {selectedProject.documents.other.map((doc) => (
                        <ListItem key={doc.id || doc.document_id}>
                          <ListItemIcon>
                            <DescriptionIcon />
                          </ListItemIcon>
                          <ListItemText
                            primary={doc.title}
                            secondary={`${doc.document_type || 'Document'} • Uploaded on ${doc.created_at ? new Date(doc.created_at).toLocaleDateString() : 'N/A'}`}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}
              </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleProjectDialogClose}>Cancel</Button>
          <Button 
            onClick={handleProjectSubmit} 
            color="primary" 
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : (selectedProject ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assign User to Project Dialog */}
      <Dialog 
        open={assignDialogOpen} 
        onClose={handleAssignDialogClose}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          Assign User to Project
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          <FormControl fullWidth margin="normal">
            <InputLabel>Project</InputLabel>
            <Select
              name="project_id"
              value={assignFormData.project_id}
              onChange={handleAssignFormChange}
              label="Project"
              required
            >
              {projects.map((project) => (
                <MenuItem key={project.id} value={project.id}>
                  {project.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <FormControl fullWidth margin="normal">
            <InputLabel>User</InputLabel>
            <Select
              name="user_id"
              value={assignFormData.user_id}
              onChange={handleAssignFormChange}
              label="User"
              required
            >
              {users.map((user) => (
                <MenuItem key={user.id} value={user.id}>
                  {user.full_name} ({user.email})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <FormControl fullWidth margin="normal">
            <InputLabel>Role in Project</InputLabel>
            <Select
              name="role"
              value={assignFormData.role}
              onChange={handleAssignFormChange}
              label="Role in Project"
            >
              <MenuItem value="member">Member</MenuItem>
              <MenuItem value="lead">Lead</MenuItem>
              <MenuItem value="developer">Developer</MenuItem>
              <MenuItem value="tester">Tester</MenuItem>
              <MenuItem value="stakeholder">Stakeholder</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleAssignDialogClose}>Cancel</Button>
          <Button 
            onClick={handleAssignSubmit} 
            color="primary" 
            variant="contained"
            disabled={loading || !assignFormData.project_id || !assignFormData.user_id}
          >
            {loading ? <CircularProgress size={24} /> : 'Assign'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            {itemToDelete?.type === 'user' ? 
              `Are you sure you want to delete the user "${itemToDelete.item.full_name}"?` :
            itemToDelete?.type === 'project' ? 
              `Are you sure you want to delete the project "${itemToDelete.item.name}"?` :
            itemToDelete?.type === 'assignment' ?
              `Are you sure you want to remove this user from the project?` :
              'Are you sure you want to delete this item?'
            }
            {itemToDelete?.type === 'user' && (
              <Box component="span" sx={{ display: 'block', color: 'error.main', mt: 2 }}>
                Warning: This will also remove the user from all assigned projects.
              </Box>
            )}
            {itemToDelete?.type === 'project' && (
              <Box component="span" sx={{ display: 'block', color: 'error.main', mt: 2 }}>
                Warning: This will also delete all documents associated with this project.
              </Box>
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleDelete} 
            color="error"
            variant="contained"
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Confirmation Dialog */}
      <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ ...confirmDialog, open: false })} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <DeleteIcon color="error" />
          {confirmDialog.title}
        </DialogTitle>
        <DialogContent>
          <Typography>{confirmDialog.message}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ ...confirmDialog, open: false })}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={async () => {
              setConfirmDialog({ ...confirmDialog, open: false });
              if (confirmDialog.onConfirm) await confirmDialog.onConfirm();
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminPanel;
